import { css, html, LitElement } from "lit";
import { property, state } from "lit/decorators.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "../../shared/components/_share-dialog-dependencies";
import "../../shared/components/progress-bar";
import "../beam-partner-logos";
import { cssReset } from "../../shared/css-reset";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { getImpact } from "../../api-sdk/v3/routes";
import { _loading } from "../../shared/fragments/loading-template";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES, WIDGET_NAMES } from "../../api-sdk/constants";
import { MissingConfig } from "../../utils/beam-errors";
import { TNumericId } from "../../shared/types";
import { LANGUAGES } from "../../api-sdk/types";
import { strings } from "../../shared/components/strings/share-button";
import { enforceConfig } from "../../shared/enforce-config";
import { urlToFile } from "../../shared/files";
import { postSocialShareAnalyticsEvent } from "../../api-sdk/v2/users/events";

type RequiredConfig = {
  apiKey: string;
  storeId: TNumericId;
  nonprofitId: TNumericId;
};

export class BeamSocialShare extends LitElement {
  static tagName = "beam-social-share";

  @property({ type: String }) public apiKey?: RequiredConfig["apiKey"];
  @property({ type: Number }) storeId?: RequiredConfig["storeId"];
  @property({ type: Number }) nonprofitId?: RequiredConfig["nonprofitId"];
  @property({ type: String }) public baseUrl = DEFAULT_BASE_URL;
  @property({ type: String }) public lang: LANGUAGES = "en";
  @property({ type: Boolean }) public debug = false;
  @property({ type: String }) transactionId?: string;

  @state() private brandName: string | null = null;
  @state() private shareImageUrl: string | null = null;
  @state() private dataUrl?: string;
  @state() private file?: File;
  @state() private isMobile = false;
  @state() private windowResized = false;

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.checkViewport);
    this.checkViewport();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.checkViewport);
    super.disconnectedCallback();
  }

  checkViewport = () => {
    this.windowResized = window.innerWidth <= 768;
  };

  private async fetchImpactData() {
    if (!enforceConfig<RequiredConfig>(["apiKey", "storeId", "nonprofitId"], this)) {
      throw new MissingConfig();
    }
    try {
      const res = await getImpact({
        baseUrl: this.baseUrl,
        headers: { authorization: `Api-Key ${this.apiKey}` },
        queryParams: {
          storeId: this.storeId,
          personalImpactLimit: 0,
          nonprofitId: this.nonprofitId,
          widgetName: WIDGET_NAMES.community_impact,
          version: "1.0.0",
          lang: this.configLang,
          transactionId: this.transactionId ?? undefined,
        },
      });
      return res;
    } catch (error) {
      if (this.debug) {
        console.error("Failed to fetch impact data", error);
      }
      throw error;
    }
  }

  // Updates component state with fetched impact data
  private async updateImpactData() {
    try {
      const data = await this.fetchImpactData();
      if (data) {
        this.brandName = data.chain.name;
        this.shareImageUrl = data.promoAssetUrl || data.personalImpactAssetUrl || data.community[0].share.imageUrl;

        if (this.shareImageUrl) {
          const file = await urlToFile(this.shareImageUrl);
          this.file = file;
          this.dataUrl = URL.createObjectURL(file);
        }
      }
    } catch (error) {
      if (this.debug) {
        console.error("Error updating impact data:", error);
      }
    }
  }

  async firstUpdated() {
    await this.updateImpactData();
    this.isMobile =
      /Mobi|Android/i.test(navigator.userAgent) || "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }

  private async openSystemShareSheet(imageUrl: string) {
    if (!imageUrl) return;

    try {
      const file = await urlToFile(imageUrl);
      if (navigator.share && this.brandName) {
        await navigator.share({
          files: [file],
          title: strings[this.lang].impactShareTitle(),
          text: strings[this.lang].impactShareText({ brandName: this.brandName }),
        });
      }
    } catch (error) {
      if (this.debug) {
        console.error("Failed to open share sheet", error);
      }
    }
  }

  private async handleClickCopy() {
    if (this.file) {
      try {
        if (this.apiKey && this.transactionId) {
          void postSocialShareAnalyticsEvent({
            baseUrl: this.baseUrl,
            apiKey: this.apiKey || "",
            requestBody: {
              eventName: "ExtensibilityCopyImageButtonClick",
              nonprofitId: this.nonprofitId,
              storeId: this.storeId,
              timestamp: Date.now(),
              transactionId: Number(this.transactionId),
            },
          }).catch(() => null);
        } else {
          console.error("Missing data for social share analytics. Skipping send");
        }
        await navigator.clipboard.write([new ClipboardItem({ "image/png": this.file })]);
      } catch (error) {
        if (this.debug) {
          console.error("Failed to copy image to clipboard:", error);
        }
      }
    }
  }

  private handleClickDownload(event: Event) {
    if (this.apiKey && this.transactionId) {
      void postSocialShareAnalyticsEvent({
        baseUrl: this.baseUrl,
        apiKey: this.apiKey || "",
        requestBody: {
          eventName: "ExtensibilityDownloadImageButtonClick",
          nonprofitId: this.nonprofitId,
          storeId: this.storeId,
          timestamp: Date.now(),
          transactionId: Number(this.transactionId),
        },
      }).catch(() => null);
    } else {
      console.error("Missing data for social share analytics. Skipping send");
    }
    const target = event.currentTarget as HTMLAnchorElement;
    if (this.isMobile && this.shareImageUrl) {
      event.preventDefault();
      this.openSystemShareSheet(this.shareImageUrl);
    } else if (this.dataUrl) {
      target.href = this.dataUrl;
      target.download = strings[this.lang].impactShareTitle();
    }
  }

  render() {
    if (!this.brandName || !this.shareImageUrl || !this.dataUrl) {
      return _loading();
    }

    return html`
      <div class="share-container ${this.windowResized || this.isMobile ? "mobile" : "desktop"}">
        <h2 class="share-title">${strings[this.lang].impactShareTitle()}</h2>
        <p class="share-text">${strings[this.lang].impactShareText({ brandName: this.brandName })}</p>
        <img
          data-speedsize-ignore="true"
          src="${this.shareImageUrl}"
          alt="${strings[this.lang].impactShareTitle()}"
          class="share-image"
        />
        <div class="share-actions">
          <button class="plain-button button share-action-button" @click="${this.handleClickCopy}">
            <sl-icon
              library="lucide"
              name="copy"
              label="${strings[this.lang].impactShareCopy()}"
              class="share-action-icon"
            ></sl-icon>
            <div class="share-action-text">${strings[this.lang].impactShareCopy()}</div>
          </button>
          <a
            class="plain-button button share-action-button plain-link"
            download="${strings[this.lang].impactShareTitle()}"
            href="${this.dataUrl}"
            @click="${this.handleClickDownload}"
          >
            <sl-icon
              library="lucide"
              name="download"
              label="${strings[this.lang].impactShareDownload()}"
              class="share-action-icon"
            ></sl-icon>
            <div class="share-action-text">${strings[this.lang].impactShareDownload()}</div>
          </a>
        </div>
      </div>
    `;
  }

  static styles = [
    cssReset,
    css`
      @font-face {
        font-family: "Mabry-Bold";
        src: url("../../../assets/fonts/mabry-bold.otf") format("opentype");
        font-weight: bold;
        font-style: normal;
      }
      @font-face {
        font-family: "Mabry-Medium";
        src: url("../../../assets/fonts/mabry-medium.otf") format("opentype");
        font-weight: bold;
        font-style: normal;
      }
      :host {
        align-items: center;
        justify-content: center;
        display: flex;
        box-sizing: border-box;
        width: 100%;
      }

      .share-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        max-width: 700px;
        text-align: center;
      }

      .share-container.mobile {
        padding: 1rem;
        max-width: 100%;
      }

      .share-title {
        font-family: "Mabry-Bold", sans-serif;
        font-size: 2.5rem;
        font-weight: bold;
        word-break: break-word;
        margin: 10px 0;
      }

      .share-title.mobile {
        font-size: 1.5rem;
      }

      .share-text {
        font-family: "Mabry-Medium", sans-serif;
        font-size: 1.25rem;
        line-height: 1.4;
        word-break: break-word;
        margin: 10px 0;
        max-width: 500px;
      }

      .share-text.mobile {
        font-size: 1rem;
      }

      .share-image {
        border-radius: 8px;
        width: 100%;
        max-width: 300px;
        height: auto;
        display: block;
        margin: 20px auto;
      }

      .share-actions {
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 10px;
        align-items: center;
        margin: 20px 0;
      }

      .share-actions.mobile {
        flex-direction: column;
      }

      .share-action-button {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px 20px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: #fff;
        text-decoration: none;
        color: #333;
        cursor: pointer;
        transition:
          background-color 0.3s,
          border-color 0.3s;
      }

      .share-action-button:hover {
        background-color: #f0f0f0;
        border-color: #bbb;
      }

      .share-action-icon {
        font-size: 1.25rem;
        margin-right: 8px;
      }

      .share-action-text {
        font-family: "Mabry-Medium", sans-serif;
        font-size: 1rem;
        line-height: 1.4;
        margin-top: 5px;
      }

      @media (max-width: 600px) {
        .share-title {
          font-size: 1.5rem;
        }

        .share-text {
          font-size: 1rem;
        }

        .share-image {
          max-width: 240px;
        }

        .share-actions {
          flex-direction: row;
          width: 50%;
          margin: 10px 0;
        }

        .share-action-button {
          width: 100%;
          margin: 5px 0;
        }
      }
    `,
  ];
}

defineCustomElement(BeamSocialShare);

declare global {
  interface HTMLElementTagNameMap {
    "beam-social-share": BeamSocialShare;
  }
}
